import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal } from "@zfp-akademie-a-s/zfp-design";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon, DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import {
  type ConsumptionExpenseFormType,
  getConsumptionExpenseFormSchema,
} from "Forms/Schemas/Budget/Expenses/consumptionExpenseFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getGetConsumptionExpenseQueryKey,
  useGetConsumptionExpense,
  useReplaceConsumptionExpense,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  type ConsumptionAmount,
  ConsumptionAmountAmountType,
  ConsumptionExpenseEntryMode,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type RefObject, forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ExpenseFormHandle } from "../Components/EditExpenseDrawer";

type Props = {
  onClose: () => void;
  ref?: RefObject<ExpenseFormHandle>;
};

export const ConsumptionExpenseForm = forwardRef<ExpenseFormHandle, Props>(({ onClose }, ref) => {
  const { readOnly } = useContext(AnalysisDetailContext);

  const { t } = useResource();
  const [detailed, setDetailed] = useState(true);
  const [openDeleteBulkModal, setOpenDeleteBulkModal] = useState<boolean>(false);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: consumptionExpenseData } = useGetConsumptionExpense(analysisId, versionId);
  const { mutateAsync: replaceConsumptionExpenseAsync } = useReplaceConsumptionExpense();

  const { control, setValue, watch, handleSubmit, clearErrors } = useForm<ConsumptionExpenseFormType>({
    resolver: yupResolver(getConsumptionExpenseFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      aggregatedAmount: (consumptionExpenseData?.aggregatedAmount?.value.toString() as any) || "",
      customItems: [],
      detailed: true,
    },
  });

  const {
    fields: customFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "customItems",
  });

  const customItems = watch("customItems");
  const food = watch("food");
  const drugstore = watch("drugstore");
  const telecommunication = watch("telecommunication");
  const cigarettes = watch("cigarettes");
  const vehicle = watch("vehicle");
  const children = watch("children");
  const gifts = watch("gifts");

  useEffect(() => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(food ?? "0") || 0) +
        (Number.parseFloat(drugstore ?? "0") || 0) +
        (Number.parseFloat(telecommunication ?? "0") || 0) +
        (Number.parseFloat(cigarettes ?? "0") || 0) +
        (Number.parseFloat(vehicle ?? "0") || 0) +
        (Number.parseFloat(children ?? "0") || 0) +
        (Number.parseFloat(gifts ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  }, [customItems, food, drugstore, telecommunication, cigarettes, vehicle, children, gifts, detailed, setValue]);

  const recalculateTotal = () => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(food ?? "0") || 0) +
        (Number.parseFloat(drugstore ?? "0") || 0) +
        (Number.parseFloat(telecommunication ?? "0") || 0) +
        (Number.parseFloat(cigarettes ?? "0") || 0) +
        (Number.parseFloat(vehicle ?? "0") || 0) +
        (Number.parseFloat(children ?? "0") || 0) +
        (Number.parseFloat(gifts ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteExpense: () => {
      setOpenDeleteBulkModal(true);
    },
  }));

  useEffect(() => {
    if (consumptionExpenseData) {
      handleChangeDetailed(consumptionExpenseData.entryMode === ConsumptionExpenseEntryMode.individual);
      setValue("aggregatedAmount", (consumptionExpenseData.aggregatedAmount?.value ?? 0).toString());

      const customItems: { name: string; value: string }[] = [];
      for (const expense of consumptionExpenseData.amounts) {
        switch (expense.amountType) {
          case ConsumptionAmountAmountType.food:
            setValue("food", expense.amount.value.toString());
            break;
          case ConsumptionAmountAmountType.drugstore:
            setValue("drugstore", expense.amount.value.toString());
            break;
          case ConsumptionAmountAmountType.telecommunication:
            setValue("telecommunication", expense.amount.value.toString());
            break;
          case ConsumptionAmountAmountType.cigarettes:
            setValue("cigarettes", expense.amount.value.toString());
            break;
          case ConsumptionAmountAmountType.vehicle:
            setValue("vehicle", expense.amount.value.toString());
            break;
          case ConsumptionAmountAmountType.children:
            setValue("children", expense.amount.value.toString());
            break;
          case ConsumptionAmountAmountType.gifts:
            setValue("gifts", expense.amount.value.toString());
            break;
          case ConsumptionAmountAmountType.other:
            customItems.push({
              name: expense.name,
              value: expense.amount.value.toString(),
            });
            break;
        }
      }
      replace(customItems);
    }
  }, [consumptionExpenseData, setValue, replace]);

  const handleChangeDetailed = (value: boolean) => {
    setDetailed(value);
    setValue("detailed", value);
    clearErrors();
  };

  const onSubmit = async (data: ConsumptionExpenseFormType) => {
    if (Number(data.aggregatedAmount) === 0) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseAmountZeroError));
      return;
    }

    const customItems: ConsumptionAmount[] =
      data.customItems?.map((field) => ({
        amountType: ConsumptionAmountAmountType.other,
        amount: {
          value: Number.parseFloat(field.value ?? "0") || 0,
          currencyCode: "CZK",
          frequency: AmountFrequency.monthly,
        },
        name: field.name ?? "",
      })) ?? [];

    const amounts: ConsumptionAmount[] = detailed
      ? [
          {
            amountType: ConsumptionAmountAmountType.food,
            amount: {
              value: Number.parseFloat(data.food ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Food),
          },
          {
            amountType: ConsumptionAmountAmountType.drugstore,
            amount: {
              value: Number.parseFloat(data.drugstore ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Drugstore),
          },
          {
            amountType: ConsumptionAmountAmountType.telecommunication,
            amount: {
              value: Number.parseFloat(data.telecommunication ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Telecommunication),
          },
          {
            amountType: ConsumptionAmountAmountType.cigarettes,
            amount: {
              value: Number.parseFloat(data.cigarettes ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Cigarettes),
          },
          {
            amountType: ConsumptionAmountAmountType.vehicle,
            amount: {
              value: Number.parseFloat(data.vehicle ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Vehicle),
          },
          {
            amountType: ConsumptionAmountAmountType.children,
            amount: {
              value: Number.parseFloat(data.children ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Children),
          },
          {
            amountType: ConsumptionAmountAmountType.gifts,
            amount: {
              value: Number.parseFloat(data.gifts ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Gifts),
          },
          ...customItems,
        ]
      : [];

    try {
      await replaceConsumptionExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? ConsumptionExpenseEntryMode.individual : ConsumptionExpenseEntryMode.aggregate,
          aggregatedAmount: {
            value: Number.parseFloat(data.aggregatedAmount ?? "0") || 0,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          amounts,
        },
      });

      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    } finally {
      queryClient.invalidateQueries({ queryKey: getGetConsumptionExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
    }
  };

  const onDelete = async () => {
    try {
      await replaceConsumptionExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? ConsumptionExpenseEntryMode.individual : ConsumptionExpenseEntryMode.aggregate,
          aggregatedAmount: { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
          amounts: [],
        },
      });

      queryClient.invalidateQueries({ queryKey: getGetConsumptionExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteBulkExpense = () => {
    replace([]);
    setOpenDeleteBulkModal(false);
    onDelete();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.Consumption)}
          </Typography>

          <Grid container spacing={Spacing.XL}>
            <Grid size={6}>
              <FormNumberField
                control={control}
                name="aggregatedAmount"
                label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.Aggregated)}
                valueType="text"
                scale={2}
                min={0}
                thousandsSeparator=" "
                unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                disabled={detailed || readOnly}
                placeholder="0"
                fullWidth
              />
            </Grid>

            <Grid size={6}>
              <Box display="flex" alignItems="flex-start" mt={5} height="100%">
                <FormControlLabel
                  control={<Checkbox disabled={readOnly} checked={detailed} onChange={(_, checked) => handleChangeDetailed(checked)} />}
                  label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.DetailedCheckbox)}
                />
              </Box>
            </Grid>

            {detailed && (
              <>
                <Grid size={6}>
                  <TextField
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Item)}
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Food)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="food"
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Value)}
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Drugstore)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="drugstore"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Telecommunication)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="telecommunication"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Cigarettes)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="cigarettes"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Vehicle)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="vehicle"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Children)}
                    disabled
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="children"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.ConsumptionExpenseTypes.Gifts)}
                    disabled
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="gifts"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                {customFields.map((field, idx) => (
                  <React.Fragment key={field.id}>
                    <Grid size={6}>
                      <FormTextField
                        control={control}
                        name={`customItems.${idx}.name`}
                        disabled={readOnly}
                        placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={5}>
                      <FormNumberField
                        control={control}
                        name={`customItems.${idx}.value`}
                        valueType="text"
                        scale={2}
                        min={0}
                        thousandsSeparator=" "
                        unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                        disabled={readOnly}
                        placeholder="0"
                        onChange={recalculateTotal}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={1} display="flex" alignItems="flex-start" justifyContent="center">
                      <Button disabled={readOnly} variant="text" color="error" onClick={() => remove(idx)}>
                        <DeleteIcon />
                      </Button>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid size={12}>
                  <Button variant="text" disabled={readOnly} startIcon={<AddIcon />} onClick={() => append({ name: "", value: "" })}>
                    {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.AddCustomItem)}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Stack>
      <DialogModal
        open={openDeleteBulkModal}
        onClose={() => setOpenDeleteBulkModal(false)}
        closeLabel={t(Resources.Common.Close)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpense)}
        subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseHint)}
        primaryAction={
          <Button variant="contained" disabled={readOnly} onClick={onDeleteBulkExpense}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </form>
  );
});
